import { useRolodexEdit } from 'features/fde'
import {
  EntryLookupFunction,
  useRolodexCache,
  useRolodexEntries,
} from 'features/rolodex'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ComponentMapType,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  getSortedAssetModels,
  RolodexEntryType,
  SceneConfig,
} from 'types'

type ComponentWithSceneConfig = {
  scene_config: SceneConfig
}

type GeckoComponentWithScene = GeckoComponentModel & ComponentWithSceneConfig

interface MultiComponentViewContextType {
  viewer: 'configuration' | 'inspection'
  unit: GeckoUnit | undefined
  site: GeckoSite | undefined
  organization: GeckoOrganization | undefined
  assetModel: GeckoAssetModel | undefined
  components: GeckoComponent[]
  componentModels: GeckoComponentModel[]
  componentDefaults: GeckoComponentDefaults[]
  componentDataFrames: GeckoComponentDataFrame[]
  getComponentDataFrame: EntryLookupFunction<GeckoComponentDataFrame>
  componentDataSets: GeckoComponentDataset[]
  inspections: GeckoInspection[]
  getInspection: EntryLookupFunction<GeckoInspection>
  latestInspection: GeckoInspection
  markupLayers: GeckoMarkupLayer[]
  visibleMarkupLayers: Map<string, GeckoMarkupLayer[]>
  setVisibleMarkupLayers: (
    componentId: string,
    visibleLayers: GeckoMarkupLayer[]
  ) => void
  allVisibleLayers: GeckoMarkupLayer[]

  selectedComponentId: string | undefined
  mode: '2D' | '3D'
  setMode: (mode: '2D' | '3D') => void
  addComponentToScene: (componentId: string) => void
  setSelectedComponentId: (componentId: string | undefined) => void
  removeComponentFromScene: (componentId: string) => void
  updateSceneConfig: (componentId: string, newConfig: SceneConfig) => void
  saveSceneConfig: () => Promise<void>
  forceRefreshAssetModelData: () => Promise<void>
  resetSceneConfig: (componentId: string) => Promise<void>
  componentNotMultiComponent: (
    componentMapEntry: ComponentMapType | undefined
  ) => boolean | undefined
}

const MultiComponentViewContext = createContext<
  MultiComponentViewContextType | undefined
>(undefined)

export const MultiComponentViewProvider: React.FC<{
  unitID: string
  viewer: 'inspection' | 'configuration'
  children: React.ReactNode
  // Optional props for controlling state externally (e.g., via URL params)
  mode?: '2D' | '3D'
  selectedComponentId?: string
  onModeChange?: (mode: '2D' | '3D') => void
  onSelectedComponentIdChange?: (componentId: string | undefined) => void
}> = ({
  unitID,
  viewer,
  children,
  mode: externalMode,
  selectedComponentId: externalSelectedComponentId,
  onModeChange,
  onSelectedComponentIdChange,
}) => {
  const {
    getUnit,
    assetModels,
    components,
    componentDatasets,
    componentDefaults,
    getComponentDataFrame,
    componentDataFrames,
    inspections,
    getInspection,
    forceRefresh: forceRefreshAssetModelData,
  } = useRolodexEntries({
    type: [
      RolodexEntryType.UNIT,
      RolodexEntryType.ASSET_MODEL,
      RolodexEntryType.COMPONENT,
      RolodexEntryType.COMPONENT_DATA_FRAME,
      RolodexEntryType.COMPONENT_DATASET,
      RolodexEntryType.COMPONENT_DEFAULTS,
      RolodexEntryType.INSPECTION,
    ],
    tags: { unit: unitID },
  })
  const { getSite, getOrganization } = useRolodexEntries({
    type: [RolodexEntryType.ORGANIZATION, RolodexEntryType.SITE],
  })

  const { unit, site, organization } = useMemo(() => {
    const unit = getUnit(unitID)
    const site = getSite(unit?.tags.site)
    const organization = getOrganization(unit?.tags.organization)
    return { unit, site, organization }
  }, [getOrganization, getSite, getUnit, unitID])

  const { patchEntry } = useRolodexCache()
  const { rolodexPatch } = useRolodexEdit()
  const { markupLayers } = useRolodexEntries(
    unit?.tags.site
      ? {
          type: [RolodexEntryType.MARKUP_LAYER],
          tags: { [RolodexEntryType.SITE]: unit.tags.site },
        }
      : undefined
  )
  const { clearAll } = useRolodexCache()

  const assetModel = getSortedAssetModels([...assetModels])[0]

  const latestInspection = inspections.toSorted(
    (a, b) => b.data.date.valueOf() - a.data.date.valueOf()
  )[0]

  // State for visible markup layers
  const [visibleMarkupLayers, setVisibleMarkupLayersState] = useState<
    Map<string, GeckoMarkupLayer[]>
  >(new Map<string, GeckoMarkupLayer[]>())

  // Automatically set all markup layers as visible (besides internal ones) when they first become available
  useEffect(() => {
    if (markupLayers.length > 0 && visibleMarkupLayers.size === 0) {
      const newVisibleLayers = new Map<string, GeckoMarkupLayer[]>()

      // Group markup layers by component
      markupLayers.forEach((layer) => {
        const componentId = layer.tags.component
        if (layer.access_restriction === 'internal') {
          return
        }
        if (componentId) {
          if (!newVisibleLayers.has(componentId)) {
            newVisibleLayers.set(componentId, [])
          }
          newVisibleLayers.get(componentId)!.push(layer)
        }
      })

      setVisibleMarkupLayersState(newVisibleLayers)
    }
  }, [markupLayers, visibleMarkupLayers.size])

  // Function to update visible layers for a specific component
  const setVisibleMarkupLayers = useCallback(
    (componentId: string, visibleLayers: GeckoMarkupLayer[]) => {
      setVisibleMarkupLayersState((prev) => {
        const newMap = new Map(prev)
        newMap.set(componentId, visibleLayers)
        return newMap
      })
    },
    []
  )

  // Collect all visible layers for AssetViz
  const allVisibleLayers = useMemo(() => {
    const allLayers: GeckoMarkupLayer[] = []
    visibleMarkupLayers.forEach((layers) => {
      allLayers.push(...layers)
    })
    return allLayers
  }, [visibleMarkupLayers])

  const updateSceneConfig = useCallback(
    async (componentId: string, newConfig: SceneConfig) => {
      if (!assetModel) return

      try {
        // Create a new Map with the updated component
        const updatedComponents = new Map(assetModel.data.components)
        const componentToUpdate = updatedComponents.get(
          componentId
        ) as GeckoComponentWithScene
        if (componentToUpdate) {
          componentToUpdate.scene_config = newConfig
          updatedComponents.set(componentId, componentToUpdate)
        }

        // Update the asset model with the new components Map
        assetModel.data.components = updatedComponents

        // Use patchEntry for live updates
        await patchEntry({
          id: assetModel.id,
          data: {
            components: {
              [componentId]: {
                scene_config: newConfig,
              },
            },
          },
        })
      } catch (error) {
        console.error('Failed to update scene config:', error)
      }
    },
    [assetModel, patchEntry]
  )

  const addComponentToScene = useCallback(
    async (componentId: string) => {
      if (assetModel) {
        const defaultConfig: SceneConfig = {
          config_2d: {
            position: [0, 0],
            rotation: 0,
          },
          config_3d: {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
          },
        }

        try {
          const updatedComponents = new Map(assetModel.data.components)
          const componentToUpdate = updatedComponents.get(
            componentId
          ) as GeckoComponentWithScene
          if (componentToUpdate) {
            componentToUpdate.scene_config = defaultConfig
            updatedComponents.set(componentId, componentToUpdate)
            assetModel.data.components = updatedComponents
          }

          await rolodexPatch([
            {
              id: assetModel.id,
              data: {
                components: {
                  [componentId]: {
                    scene_config: defaultConfig,
                  },
                },
              },
            },
          ])

          // Force refresh to ensure changes are reflected
          await forceRefreshAssetModelData()
        } catch (error) {
          console.error('Failed to add component to scene:', error)
        }
      }
    },
    [assetModel, rolodexPatch, forceRefreshAssetModelData]
  )

  const removeComponentFromScene = useCallback(
    async (componentId: string) => {
      if (assetModel) {
        try {
          const updatedComponents = new Map(assetModel.data.components)
          const componentToUpdate = updatedComponents.get(componentId)
          if (componentToUpdate) {
            const componentWithoutScene =
              componentToUpdate as GeckoComponentModel & {
                scene_config?: undefined
                sceneConfig?: undefined
              }
            delete componentWithoutScene.scene_config
            delete componentWithoutScene.sceneConfig
            updatedComponents.set(componentId, componentToUpdate)
            assetModel.data.components = updatedComponents
          }

          await rolodexPatch([
            {
              id: assetModel.id,
              data: {
                components: {
                  [componentId]: {
                    scene_config: null,
                  },
                },
              },
            },
          ])

          await forceRefreshAssetModelData()
        } catch (error) {
          console.error('Failed to remove component from scene:', error)
        }
      }
    },
    [assetModel, rolodexPatch, forceRefreshAssetModelData]
  )

  const setMode = useCallback(
    (mode: '2D' | '3D') => {
      if (onModeChange) {
        onModeChange(mode)
      }
    },
    [onModeChange]
  )

  const setSelectedComponentId = useCallback(
    (componentId: string | undefined) => {
      if (onSelectedComponentIdChange) {
        onSelectedComponentIdChange(componentId)
      }
    },
    [onSelectedComponentIdChange]
  )

  const resetSceneConfig = useCallback(
    async (componentId: string) => {
      if (!assetModel) return

      try {
        // Clear the cache first to remove any pending changes
        await clearAll()

        // Get the original scene config from the raw data
        const rawComponents = assetModel.raw?.data.components as Record<
          string,
          { scene_config: SceneConfig }
        >
        const originalComponent = rawComponents?.[componentId]
        if (!originalComponent) {
          throw new Error('Component not found in raw data')
        }

        const originalSceneConfig = originalComponent.scene_config
        if (!originalSceneConfig) {
          throw new Error('Scene config not found in raw data')
        }

        // Create a new Map with the updated component
        const updatedComponents = new Map(assetModel.data.components)
        const componentToUpdate = updatedComponents.get(
          componentId
        ) as GeckoComponentWithScene
        if (componentToUpdate) {
          componentToUpdate.scene_config = originalSceneConfig
          updatedComponents.set(componentId, componentToUpdate)
        }

        // Update the asset model with the new components Map
        assetModel.data.components = updatedComponents

        // Update the cache with the original values
        await patchEntry({
          id: assetModel.id,
          data: {
            components: {
              [componentId]: {
                scene_config: originalSceneConfig,
              },
            },
          },
        })

        // Save the changes to Rolodex to ensure persistence
        await rolodexPatch([
          {
            id: assetModel.id,
            data: {
              components: {
                [componentId]: {
                  scene_config: originalSceneConfig,
                },
              },
            },
          },
        ])

        // Force refresh to ensure all components are updated with the reset values
        await forceRefreshAssetModelData()
      } catch (error) {
        console.error('Failed to reset scene config:', error)
        throw error
      }
    },
    [assetModel, clearAll, forceRefreshAssetModelData, patchEntry, rolodexPatch]
  )

  const saveSceneConfig = useCallback(async () => {
    if (!assetModel) return

    try {
      // Get components that have scene configs defined
      const componentsWithSceneConfig = Array.from(
        assetModel.data.components.values()
      ).filter((component) => {
        const sceneConfig =
          component.sceneConfig ??
          (component.json?.scene_config as SceneConfig | undefined)
        // A component is in the scene if it has a scene config defined
        return !!sceneConfig
      })

      // Create updates for components with scene configs
      const updates = componentsWithSceneConfig.map((component) => ({
        id: assetModel.id,
        data: {
          components: {
            [component.id]: {
              scene_config:
                component.sceneConfig ?? component.json?.scene_config,
            },
          },
        },
      }))

      // Save to Rolodex
      await rolodexPatch(updates)

      // Force refresh to ensure changes are reflected
      await forceRefreshAssetModelData()
    } catch (error) {
      console.error('Failed to save scene config:', error)
    }
  }, [assetModel, rolodexPatch, forceRefreshAssetModelData])

  // This check will go away once we no longer have GeckoComponent usage + no more legacy cone
  const componentNotMultiComponent = useCallback(
    (componentMapEntry: ComponentMapType | undefined) => {
      const component = components.find(
        (c) => c.id === componentMapEntry?.componentModel?.id
      )
      return (
        componentMapEntry?.componentModel?.type ===
          GeckoComponentModelType.LegacyCone || component?.data.disable_3d
      )
    },
    [components]
  )

  const value = useMemo(
    () => ({
      assetModel,
      unit,
      site,
      organization,
      latestInspection,
      viewer,
      markupLayers: [...markupLayers],
      visibleMarkupLayers,
      setVisibleMarkupLayers,
      allVisibleLayers,
      mode: externalMode || '3D',
      components: [...components],
      componentModels: Array.from(assetModel?.data.components.values() ?? []),
      selectedComponentId: externalSelectedComponentId,
      componentDataFrames: [...componentDataFrames],
      componentDataSets: [...componentDatasets],
      componentDefaults: [...componentDefaults],
      inspections: [...inspections],
      addComponentToScene,
      removeComponentFromScene,
      updateSceneConfig,
      setMode,
      setSelectedComponentId,
      forceRefreshAssetModelData,
      resetSceneConfig,
      getComponentDataFrame,
      getInspection,
      saveSceneConfig,
      componentNotMultiComponent,
    }),
    [
      assetModel,
      unit,
      site,
      organization,
      latestInspection,
      viewer,
      markupLayers,
      visibleMarkupLayers,
      setVisibleMarkupLayers,
      allVisibleLayers,
      externalMode,
      externalSelectedComponentId,
      components,
      componentDataFrames,
      componentDatasets,
      componentDefaults,
      inspections,
      addComponentToScene,
      removeComponentFromScene,
      updateSceneConfig,
      setMode,
      setSelectedComponentId,
      forceRefreshAssetModelData,
      resetSceneConfig,
      getComponentDataFrame,
      getInspection,
      saveSceneConfig,
      componentNotMultiComponent,
    ]
  )

  return (
    <MultiComponentViewContext.Provider value={value}>
      {children}
    </MultiComponentViewContext.Provider>
  )
}

export const useMultiComponentView = () => {
  const context = useContext(MultiComponentViewContext)
  if (!context) {
    throw new Error(
      'useMultiComponentView must be used within a MultiComponentViewProvider'
    )
  }
  return context
}
